/**
 * น้ำหนักของการโจมตี (data/hitWeight.ts): a landed blow shoves its target back along the blow and, when heavy (a break
 * that lands, a won interrupt, a power move), staggers it with a short stun. Only the monsters `HIT_WEIGHT.monsters`
 * names take it (the yard's sparring pupils), never the player, a dummy or the dead.
 *
 * - `observe` (`hit`): the push is queued as a velocity along src → target whose eased-out integral is the class's
 *   `knock`; a heavy blow raises the target's `stunT` to the class's `stun` (a longer stun already taken stands).
 * - `step`: each queued push moves its target through the World's own `move` (walls, water and heights stop it), its
 *   facing kept: a shove, not a turn. A push ends after `HIT_WEIGHT.knockTime`.
 * No draw is made from the random stream.
 */
import { HIT_WEIGHT, hitWeightOf } from '../data/hitWeight';
import type { WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

interface Push {
  vx: number;
  vy: number;
  left: number;
}

/** The ease of the push: v·e^(−K t), whose integral over the push is about v/K. */
const K = 10;

export class HitWeightSystem implements WorldSystem {
  readonly name = 'hitWeight';
  private readonly pushes = new Map<number, Push>();

  constructor(private readonly h: SystemHost) {}

  observe(ev: WorldEvent): void {
    if (ev.type !== 'hit') return;
    const w = this.h.world;
    const tgt = w.get(ev.tgtId);
    if (!tgt || tgt.dead || tgt.kind !== 'monster' || !HIT_WEIGHT.monsters.includes(tgt.brain?.defId ?? '')) return;
    const hw = hitWeightOf(ev.skillId, ev.outcome);
    if (!hw) return;
    const src = w.get(ev.srcId);
    let dx = src ? tgt.x - src.x : -tgt.dirX;
    let dy = src ? tgt.y - src.y : -tgt.dirY;
    const l = Math.hypot(dx, dy);
    if (l < 1e-6) {
      dx = -tgt.dirX;
      dy = -tgt.dirY;
    }
    const n = Math.hypot(dx, dy) || 1;
    const v = (hw.knock * K) / (1 - Math.exp(-K * HIT_WEIGHT.knockTime));
    this.pushes.set(tgt.id, { vx: (dx / n) * v, vy: (dy / n) * v, left: HIT_WEIGHT.knockTime });
    if (hw.stun > 0 && ev.outcome !== 'blocked') {
      tgt.stunT = Math.max(tgt.stunT, hw.stun * (100 / (100 + tgt.toughness)));
      tgt.blocking = false;
      tgt.cast = null;
      if (tgt.brain) tgt.brain.blockT = 0;
    }
  }

  step(dt: number): void {
    if (!this.pushes.size) return;
    const w = this.h.world;
    for (const [id, p] of this.pushes) {
      const e = w.get(id);
      if (!e || e.dead || p.left <= 0) {
        this.pushes.delete(id);
        continue;
      }
      const t = Math.min(dt, p.left);
      const dirX = e.dirX;
      const dirY = e.dirY;
      this.h.move(e, p.vx * t, p.vy * t);
      e.dirX = dirX;
      e.dirY = dirY;
      const ease = Math.exp(-K * t);
      p.vx *= ease;
      p.vy *= ease;
      p.left -= t;
    }
  }
}
